import { useState } from "react";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  isWithinInterval,
  isBefore,
  startOfDay,
  parseISO
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function AvailabilityCalendar({ 
  disponibilites = [], 
  onSelectDates,
  selectedArrivee,
  selectedDepart
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = startOfDay(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get the first day of the week for proper alignment
  const startDay = monthStart.getDay();
  const emptyDays = startDay === 0 ? 6 : startDay - 1; // Adjust for Monday start

  const isDateBlocked = (date) => {
    return disponibilites.some(dispo => {
      const start = parseISO(dispo.date_debut);
      const end = parseISO(dispo.date_fin);
      return isWithinInterval(date, { start, end }) || isSameDay(date, start) || isSameDay(date, end);
    });
  };

  const isDatePast = (date) => {
    return isBefore(date, today);
  };

  const isDateSelected = (date) => {
    if (selectedArrivee && isSameDay(date, selectedArrivee)) return "arrivee";
    if (selectedDepart && isSameDay(date, selectedDepart)) return "depart";
    if (selectedArrivee && selectedDepart) {
      if (isWithinInterval(date, { start: selectedArrivee, end: selectedDepart })) {
        return "range";
      }
    }
    return false;
  };

  const handleDayClick = (date) => {
    if (isDatePast(date) || isDateBlocked(date)) return;

    if (!selectedArrivee || (selectedArrivee && selectedDepart)) {
      // Start new selection
      onSelectDates(date, null);
    } else if (selectedArrivee && !selectedDepart) {
      if (isBefore(date, selectedArrivee)) {
        // If clicked date is before arrivee, make it the new arrivee
        onSelectDates(date, null);
      } else {
        // Check if any blocked date is in between
        const hasBlockedInRange = disponibilites.some(dispo => {
          const start = parseISO(dispo.date_debut);
          const end = parseISO(dispo.date_fin);
          return (
            isWithinInterval(start, { start: selectedArrivee, end: date }) ||
            isWithinInterval(end, { start: selectedArrivee, end: date })
          );
        });

        if (hasBlockedInRange) {
          // Reset and start new selection
          onSelectDates(date, null);
        } else {
          onSelectDates(selectedArrivee, date);
        }
      }
    }
  };

  const getDayClasses = (date) => {
    const isPast = isDatePast(date);
    const isBlocked = isDateBlocked(date);
    const selected = isDateSelected(date);
    const isToday = isSameDay(date, today);

    let classes = "w-10 h-10 flex items-center justify-center text-sm transition-all ";

    if (isPast) {
      classes += "text-gray-300 cursor-not-allowed ";
    } else if (isBlocked) {
      classes += "bg-red-100 text-red-400 cursor-not-allowed line-through ";
    } else if (selected === "arrivee") {
      classes += "bg-[#004333] text-white font-bold cursor-pointer ";
    } else if (selected === "depart") {
      classes += "bg-[#C9922A] text-white font-bold cursor-pointer ";
    } else if (selected === "range") {
      classes += "bg-[#004333]/10 text-[#004333] cursor-pointer ";
    } else {
      classes += "hover:bg-[#C9922A]/20 cursor-pointer text-gray-700 ";
    }

    if (isToday && !selected) {
      classes += "ring-2 ring-[#C9922A] ring-offset-1 ";
    }

    return classes;
  };

  const weekDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  return (
    <div className="bg-white border border-gray-200 p-6" data-testid="availability-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="p-2 hover:bg-gray-100 transition-colors"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="w-5 h-5 text-[#004333]" />
        </button>
        <h3 className="font-display text-lg text-[#004333] capitalize">
          {format(currentMonth, "MMMM yyyy", { locale: fr })}
        </h3>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="p-2 hover:bg-gray-100 transition-colors"
          data-testid="calendar-next-month"
        >
          <ChevronRight className="w-5 h-5 text-[#004333]" />
        </button>
      </div>

      {/* Week days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for alignment */}
        {[...Array(emptyDays)].map((_, index) => (
          <div key={`empty-${index}`} className="w-10 h-10" />
        ))}

        {/* Actual days */}
        {days.map((day) => (
          <button
            key={day.toISOString()}
            onClick={() => handleDayClick(day)}
            disabled={isDatePast(day) || isDateBlocked(day)}
            className={getDayClasses(day)}
            data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
          >
            {format(day, "d")}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#004333]"></div>
            <span className="text-gray-600">Arrivée</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#C9922A]"></div>
            <span className="text-gray-600">Départ</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#004333]/10"></div>
            <span className="text-gray-600">Séjour</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100"></div>
            <span className="text-gray-600">Indisponible</span>
          </div>
        </div>
      </div>

      {/* Selected dates summary */}
      {(selectedArrivee || selectedDepart) && (
        <div className="mt-4 p-4 bg-[#FAFAF8]">
          <div className="flex justify-between text-sm">
            <div>
              <span className="text-gray-500">Arrivée:</span>{" "}
              <span className="font-semibold text-[#004333]">
                {selectedArrivee ? format(selectedArrivee, "dd MMMM yyyy", { locale: fr }) : "—"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Départ:</span>{" "}
              <span className="font-semibold text-[#004333]">
                {selectedDepart ? format(selectedDepart, "dd MMMM yyyy", { locale: fr }) : "—"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
